<template>
  <div class="container pb-70">
    <div v-if="breadcrumbs.length" class="breadcrumbs-div">
      <QABreadcrumbs
        :breadcrumbs="breadcrumbs"
        class="breadcrumbs-categories"
      />
    </div>
    <MessageArea />
    <div class="container-inner mt-30">
      <QAHeaderSearch :is-hidden="step === 1 || step === 2" />
      <QACategories v-show="step === 3" :hidden-category="true" />
      <QAContentQuestion v-show="step !== 3" :data="question" class="py-15" />
      <div v-if="step === 1" class="qa-aswer-form">
        <div class="qa-title">
          <i class="fas fa-pencil-alt c-text-primary mr-5"></i>回答を編集
        </div>

        <div class="media align-items-start mt-20">
          <div class="media-left mr-25">
            <figure class="image is-64x64" style="margin-top: 6px;">
              <router-link v-if="authorId !== null" :to="{ name: 'CustomerReviewList', params: { customer_id: authorId } }">
                <img
                  :src="authorAvatar"
                  alt="author name"
                  class="img-thumbnail is-rounded"
                />
              </router-link>
              <img
                v-else
                :src="authorAvatar"
                alt="author name"
                class="img-thumbnail is-rounded"
              />
            </figure>
          </div>
          <div class="media-content content-short">
            <nav class="level mb-0">
              <!-- Left side -->
              <div class="level-left">
                <p v-if="authorId !== null" class="qa-label"><router-link :to="{ name: 'CustomerReviewList', params: { customer_id: authorId } }"><span>{{ authorName }}</span></router-link> さん</p>
              </div>

              <!-- Right side -->
              <div class="level-right">
                <p v-if="answerDate" class="font-small c-text-grey4 mb-5">{{ time }}</p>
              </div>
            </nav>
            <b-field>
              <BCustomInput
                ref="answerContent"
                v-model="answerContent"
                :validate-function="validateAnswerContent"
                rows="8"
                placeholder="５文字以上〜1000文字以内"
                type="textarea"
                class="qa-message"
              />
            </b-field>
          </div>
        </div>
        <div class="buttons is-centered mt-35">
          <div class="button is-outlined is-long font-small qa-font-2 mr-15" @click="cancel()">キャンセル</div>
          <div class="button is-primary is-long qa-font-2" @click="toConfirm()">確認</div>
        </div>
      </div>
      <div v-if="step === 2">
        <div class="qa-page-title mb-15">回答内容確認</div>
        <div class="qa-title mb-20">以下の内容で回答を投稿します。</div>
        <QAContentItem
          :id="answerId"
          :date="answerDate"
          :author-id="authorId"
          :author-avatar="authorAvatar"
          :author-name="authorName"
          :content="answerContent"
          :is-edit="false"
          type="answer"
        />
        <div class="buttons is-centered mt-35">
          <div class="button is-outlined is-long font-small qa-font-2 mr-15" @click="changeStep(1)">内容を修正</div>
          <div class="button is-primary is-long qa-font-2" @click="submit()">投稿</div>
        </div>
      </div>

      <div v-if="step === 3" class="text-center mt-30">
        <div class="qa-page-title none-border mb-40">回答の投稿が完了しました</div>
        <div class="fs-18 mb-30">
          ユーザーの質問へ回答いただきありがとうございました。<br />
          質問者によってベストアンサーに選ばれるとトークンが付与されます。
        </div>
        <div class="buttons is-centered mt-35">
          <router-link :to="{ name: 'QATop' }">
            <div class="button is-primary is-long qa-font-2">ユーザーQ&A TOPへ</div>
          </router-link>
        </div>
      </div>
    </div>
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    />
  </div>
</template>

<style lang="scss" src="../qa.scss">
</style>

<script>
import messageUtils from '@/utils/mixins/messageUtils';
import MessageArea from '@/components/common/message-area/message-area';
import { onBlur, checkError, validateInputValue, validateEmptyValue, validateValueBounded } from '@/utils/commonUtil';
import BCustomInput from '@/components/common/b-custom-input/b-custom-input.vue';
import QABreadcrumbs from '../../../../components/breadcrumbs/breadcrumbs-categories';
import QAHeaderSearch from '../components/header-search';
import QACategories from '../components/categories';
import QAContentItem from '../components/content-item';
import QAContentQuestion from '../components/content-question';
import { NOTIFY_CATEGORY } from '@/services/constants';
import { getUserIdByPublicAddress } from '@/services/user.service';
import { getUserProfile } from '@/services/profile.service';
import { getQuestionById, createAnswer, updateAnswer, getAnswerById } from '@/services/forum.service';

export default {
  name: 'QAAnswerEdit',
  components: {
    MessageArea,
    BCustomInput,
    QABreadcrumbs,
    QAHeaderSearch,
    QACategories,
    QAContentItem,
    QAContentQuestion
  },
  mixins: [messageUtils],
  props: {
    stepProp: {
      type: [String, Number, Object],
      default: () => null
    },
    contentProp: {
      type: [String, Object],
      default: () => null
    }
  },
  data() {
    return {
      step: 1,
      questionId: null,
      question: null,
      answerId: null,
      answerDate: null,
      answerContent: '',
      currentUser: null,
      breadcrumbs: []
    };
  },
  computed: {
    authorId() {
      return this.currentUser ? this.currentUser.publicAddress : null;
    },
    authorAvatar() {
      return this.currentUser && this.currentUser.avatar
        ? this.currentUser.avatar
        : require('@/assets/images/user_default.svg');
    },
    authorName() {
      return this.currentUser ? this.currentUser.userName : '';
    },
    categoryId: function() {
      return this.question && this.question.category !== undefined ? this.question.category : null;
    },
    categoryName: function() {
      let result = '';

      if (!this.question || this.question.category === null) return '';

      const categories = this.$i18n !== null ? this.$t('categories_q_a') : [];
      const category = categories.filter(item => item.value === this.question.category);

      if (category && category.length) result = category[0].name;

      return result;
    },
    time: function() {
      let result = '';

      if (!this.answerDate) return result;

      if (typeof this.answerDate === 'number') {
        result = this.$moment(new Date(this.answerDate * 1000)).format('YYYY/M/D');
      } else if (typeof this.answerDate === 'string' && this.answerDate.length === 10) {
        result = this.$moment(new Date(parseInt(this.answerDate) * 1000)).format('YYYY/M/D');
      } else {
        result = this.$moment(new Date(this.answerDate)).format('YYYY/M/D');
      }

      return result;
    }
  },
  async mounted() {
    this.questionId = this.$route.params.id ? parseInt(this.$route.params.id, 10) : null;
    this.answerId = this.$route.params.answerId ? parseInt(this.$route.params.answerId, 10) : null;

    await this.fetchUserProfile();

    if (this.currentUser && this.questionId) {
      this.fetchQuestion(this.questionId);
    }

    if (this.contentProp !== null && this.stepProp !== null) {
      this.step = this.stepProp;
      this.answerContent = this.contentProp;
    } else if (!this.currentUser || !this.answerId) {
      this.$router.push({ name: 'QuestionDetail', params: { id: this.questionId } });
    }

    if (this.currentUser && this.answerId) {
      this.fetchAnswer(this.answerId);
    }
  },
  methods: {
    initBreadcrumbs() {
      this.breadcrumbs = [
        { name: 'ホーム', link: '/' },
        { name: 'ユーザーQ&A', link: '/question-answer' },
        { name: `${this.categoryName}カテゴリーの質問一覧`, link: `/question-answer/category/${this.categoryId}` },
        { name: '質問詳細', link: `/question-answer/detail/${this.questionId}` }
      ];
    },
    makeBreadcrumbs(value) {
      this.initBreadcrumbs();
      switch (value) {
        case 1:
          this.formBreadcrumbs();
          break;

        case 2:
          this.confirmBreadcrumbs();
          break;

        case 3:
          this.completeBreadcrumbs();
          break;

        default:
          this.formBreadcrumbs();
          break;
      }
    },
    formBreadcrumbs() {
      this.breadcrumbs.push({ name: '回答編集', link: '' });
    },
    confirmBreadcrumbs() {
      this.breadcrumbs.push({ name: '回答内容確認', link: '' });
    },
    completeBreadcrumbs() {
      this.breadcrumbs.push({ name: '回答投稿完了', link: '' });
    },
    async fetchUserProfile() {
      this.clearMessage();
      const { status, result } = await getUserProfile();
      if (status) {
        this.currentUser = result.data;
      } else {
        this.setErrorMessage(result);
      }
    },
    async fetchQuestion(id) {
      this.clearMessage();
      this.loading++;

      const { status, result } = await getQuestionById(id);

      if (status) {
        this.question = {};
        this.$set(this.question, 'category', parseInt(result.data['category'], 10));
        this.$set(this.question, 'content', result.data['content']);
        this.$set(this.question, 'authorId', result.data['author']);
        this.$set(this.question, 'status', result.data['status']);
        this.$set(this.question, 'numOfAnswer', result.data['numOfAnswer']);
        this.$set(this.question, 'bestAnswerId', result.data['bestAnswerId']);
        this.$set(this.question, 'date', result.data['createdAt']);

        this.makeBreadcrumbs(this.step);

        const userId = await getUserIdByPublicAddress(result.data['author']);

        if (userId) {
          const profile = await getUserProfile({ uid: userId });

          if (profile.status) {
            this.$set(this.question, 'authorName', profile.result.data.userName);
            this.$set(this.question, 'authorAvatar', profile.result.data.avatar);
          }
        }

        this.loading--;
      } else {
        this.loading--;
        this.setErrorMessage(result);
      }
    },
    async fetchAnswer(id) {
      this.clearMessage();
      this.loading++;

      const { status, result } = await getAnswerById(id);
      this.loading--;

      if (status) {
        this.answerContent = result.data.content;
        this.answerDate = result.data.createdAt;
      } else {
        this.setErrorMessage(result);
      }
    },
    async submit() {
      this.clearMessage();
      this.loading++;

      if (!this.answerId) {
        const { status, result } = await createAnswer(this.questionId, this.answerContent);
        this.loading--;

        if (status) {
          this.changeStep(3);
        } else {
          this.setErrorMessage(result);
        }
      } else {
        const { status, result } = await updateAnswer(this.answerId, this.answerContent);
        this.loading--;

        if (status) {
          this.changeStep(3);
        } else {
          this.setErrorMessage(result);
        }
      }
    },
    toConfirm() {
      if (!this.validate()) return;

      this.changeStep(2);
    },
    cancel() {
      if (this.questionId !== null) {
        this.$router.push({ name: 'QuestionDetail', params: { id: this.questionId } });
      } else {
        this.$router.push({ name: 'QATop' });
      }
    },
    changeStep(value) {
      this.clearMessage();
      this.step = value;
      this.makeBreadcrumbs(value);
    },
    validateAnswerContent(value) {
      return validateInputValue(() => {
        validateEmptyValue(value, 'EF2173_Content');
        validateValueBounded(value, 5, 1000, 'EF2174_Content');
      });
    },
    validate() {
      const refs = ['answerContent'];

      onBlur(this.$refs, refs);
      return checkError(this.$refs);
    }
  }
};
</script>
